import DocIcons from '@/icons/DocIcons'
import ImageIcons from '@/icons/ImageIcons'
import PdfIcons from '@/icons/PdfIcons'
import PptxIcons from '@/icons/PptxIcons'
import VideoIcons from '@/icons/VideoIcons'
import XlsxIcons from '@/icons/XlsxIcons'
import { ReactElement } from 'react'
import { RecentObjectResponse } from '../types'
import dayjs from 'dayjs'

// Helper function to format file size
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// Helper function to format date
export const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })
}

export const isValidUUID = (uuid: string): boolean => {
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
        uuid
    )
}

// Retrieve object extension
// if no extension, return the name
export const extractExtension = (filename?: string) =>
    filename?.split('.').pop() || ''

// Retrieve object name from an object key/path
export const extractName = (path?: string) =>
    path?.split('/').filter(Boolean).pop() || ''

// Retrieve employee uuid from an object key/path
export const extractUuidFromObjectKey = (key: string) => key.split('/')[2]

const imageIconClass = 'text-red-500'
export const FileIcon: Record<string, ReactElement> = {
    pdf: <PdfIcons />,
    jpg: <ImageIcons className={imageIconClass} />,
    jpeg: <ImageIcons className={imageIconClass} />,
    png: <ImageIcons className={imageIconClass} />,
    gif: <ImageIcons className={imageIconClass} />,
    webp: <ImageIcons className={imageIconClass} />,
    xlsx: <XlsxIcons />,
    xls: <XlsxIcons />,
    csv: <XlsxIcons />,
    mp4: <VideoIcons />,
    mp3: <VideoIcons />,
    pptx: <PptxIcons />,
    doc: <DocIcons className="text-blue-500" />,
    docx: <DocIcons className="text-blue-500" />,
    mov: <VideoIcons />,
}

export const categorizeObjectByDate = (data: RecentObjectResponse[]) => {
    const categories = {
        today: [] as RecentObjectResponse[],
        yesterday: [] as RecentObjectResponse[],
        thisWeek: [] as RecentObjectResponse[],
        earlier: [] as RecentObjectResponse[],
    }

    for (const obj of data) {
        const viewTime = dayjs(obj.recent_object_last_view)

        if (viewTime.isToday()) {
            categories.today.push(obj)
        } else if (viewTime.isYesterday()) {
            categories.yesterday.push(obj)
        } else if (dayjs().isSame(viewTime, 'week')) {
            categories.thisWeek.push(obj)
        } else {
            categories.earlier.push(obj)
        }
    }
    return categories
}

export const setFolderName = (prefix: string, folderName: string): void => {
    sessionStorage.setItem(`folder_name_${prefix}`, folderName)
}

// Retrieve object name from an object key/path and remove extension
// if no extension, return the name
export const extractNameAndExtension = (path?: string) =>
    path?.split('/').filter(Boolean).pop()?.split('.').slice(0, -1).join('.') ||
    path?.split('/').filter(Boolean).pop() ||
    ''

// Retrieve parent path of an object
export const getParentDir = (objectKey: string) => {
    objectKey = objectKey.trim().replace(/\/+$/, '') //Remove white space and trailing slash

    if (!objectKey.includes('/')) return '' // No parent folder

    const parts = objectKey.split('/')
    parts.pop()

    return parts.join('/')
}

// Remove last element of a path
export const popPath = (path: string) => {
    path = path.trim().replace(/\/+$/, '') //Remove white space and trailing slash

    if (!path.includes('/')) return path

    const parts = path.split('/')
    parts.pop()

    return parts.join('/')
}

export const getFolderName = (prefix: string): string | null => {
    if (prefix && !prefix.endsWith('/')) {
        prefix = prefix + '/'
    }
    return sessionStorage.getItem(`folder_name_${prefix}`)
}
